<template>
  <v-main class="modern-helper-bg">
    <v-container fluid class="pa-8">
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>

      <!-- Header with Gradient -->
      <div class="mb-12 header-gradient-section">
        <h1 class="text-5xl font-bold gradient-title mb-3 animate-fade-in">
          {{ $t('helper.title') }}
        </h1>
        <p class="text-xl text-gray-600 mt-3 leading-relaxed">
          {{ $t('helper.subtitle') }}
        </p>
      </div>

      <!-- Tab Navigation with Modern Styling -->
      <v-tabs
        v-model="activeTab"
        class="mb-8 modern-tabs"
        background-color="transparent"
        color="primary-600"
      >
        <v-tab>{{ $t('helper.tabs.overview') }}</v-tab>
        <v-tab>{{ $t('helper.tabs.concepts') }}</v-tab>
        <v-tab>{{ $t('helper.tabs.standards') }}</v-tab>
        <v-tab>{{ $t('helper.tabs.tools') }}</v-tab>
        <v-tab>{{ $t('helper.tabs.workflow') }}</v-tab>
      </v-tabs>

      <!-- Tab Content -->
      <v-tabs-items v-model="activeTab" class="modern-tab-content">
        <!-- Tab 1: Overview -->
        <v-tab-item>
          <v-card class="mt-6 modern-card" elevation="0">
            <v-card-text>
              <div class="space-y-8">
                <!-- CertifAI Overview -->
                <div class="modern-section">
                  <h2 class="text-3xl font-bold section-title-blue mb-4">
                    {{ $t('helper.overview.certifaiTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.overview.certifaiDesc') }}
                  </p>
                  <v-alert type="info" outlined class="mb-4 modern-alert">
                    {{ $t('helper.overview.certifaiNote') }}
                  </v-alert>
                </div>

                <!-- Key Features -->
                <div>
                  <h3 class="text-2xl font-bold text-gray-800 mb-6">
                    {{ $t('helper.overview.features') }}
                  </h3>
                  <v-list class="bg-transparent">
                    <v-list-item
                      v-for="(feature, idx) in featuresList"
                      :key="idx"
                      class="modern-list-item"
                    >
                      <v-list-item-icon>
                        <div class="feature-icon-wrapper">
                          <v-icon color="white" class="text-xl">{{ feature.icon }}</v-icon>
                        </div>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="font-bold text-gray-900">
                          {{ feature.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-gray-600">
                          {{ feature.description }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>

                <!-- Dashboard Overview -->
                <div>
                  <h3 class="text-2xl font-bold text-gray-800 mb-4">
                    {{ $t('helper.overview.dashboard') }}
                  </h3>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.overview.dashboardDesc') }}
                  </p>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <p class="text-sm text-gray-700">
                        {{ $t('helper.overview.dashboardContent') }}
                      </p>
                    </v-card-text>
                  </v-card>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Tab 2: Concepts -->
        <v-tab-item>
          <v-card class="mt-6 modern-card" elevation="0">
            <v-card-text>
              <div class="space-y-8">
                <!-- Subject of Certification -->
                <div class="modern-section">
                  <h2 class="text-3xl font-bold section-title-blue-purple mb-4">
                    {{ $t('helper.concepts.socTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.concepts.socDesc') }}
                  </p>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <p class="font-bold mb-3 text-gray-900">
                        {{ $t('helper.concepts.socIncludes') }}
                      </p>
                      <ul class="list-disc list-inside space-y-2 text-sm text-gray-700">
                        <li>{{ $t('helper.concepts.socItem1') }}</li>
                        <li>{{ $t('helper.concepts.socItem2') }}</li>
                        <li>{{ $t('helper.concepts.socItem3') }}</li>
                        <li>{{ $t('helper.concepts.socItem4') }}</li>
                      </ul>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- Security Assurance Case -->
                <div class="modern-section">
                  <h2 class="text-3xl font-bold section-title-purple-pink mb-4">
                    {{ $t('helper.concepts.sacTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.concepts.sacDesc') }}
                  </p>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <p class="font-bold mb-3 text-gray-900">
                        {{ $t('helper.concepts.sacStructure') }}
                      </p>
                      <div class="text-sm space-y-3">
                        <p>
                          <span class="font-bold text-primary-600"
                            >{{ $t('helper.concepts.requirements') }}:</span
                          >
                          <span class="text-gray-700">{{
                            $t('helper.concepts.requirementsDesc')
                          }}</span>
                        </p>
                        <p>
                          <span class="font-bold text-primary-600"
                            >{{ $t('helper.concepts.claims') }}:</span
                          >
                          <span class="text-gray-700">{{ $t('helper.concepts.claimsDesc') }}</span>
                        </p>
                        <p>
                          <span class="font-bold text-primary-600"
                            >{{ $t('helper.concepts.subclaims') }}:</span
                          >
                          <span class="text-gray-700">{{
                            $t('helper.concepts.subclaimsDesc')
                          }}</span>
                        </p>
                        <p>
                          <span class="font-bold text-primary-600"
                            >{{ $t('helper.concepts.evidences') }}:</span
                          >
                          <span class="text-gray-700">{{
                            $t('helper.concepts.evidencesDesc')
                          }}</span>
                        </p>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- Claims, Sub-Claims & Evidence Hierarchy -->
                <div class="modern-section">
                  <h2 class="text-3xl font-bold section-title-indigo-blue mb-4">
                    {{ $t('helper.concepts.hierarchyTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.concepts.hierarchyDesc') }}
                  </p>
                  <v-card class="modern-highlight-card hierarchy-card">
                    <v-card-text>
                      <div class="font-mono text-sm space-y-2 text-gray-800">
                        <div>📋 {{ $t('helper.concepts.requirement') }}</div>
                        <div class="ml-6">└─ 📌 {{ $t('helper.concepts.claim1') }}</div>
                        <div class="ml-12">└─ 🎯 {{ $t('helper.concepts.subclaim1') }}</div>
                        <div class="ml-18">└─ 📄 {{ $t('helper.concepts.evidence1') }}</div>
                        <div class="ml-18">└─ 📄 {{ $t('helper.concepts.evidence2') }}</div>
                        <div class="ml-12">└─ 🎯 {{ $t('helper.concepts.subclaim2') }}</div>
                        <div class="ml-18">└─ 📄 {{ $t('helper.concepts.evidence3') }}</div>
                        <div class="ml-6">└─ 📌 {{ $t('helper.concepts.claim2') }}</div>
                        <div class="ml-12">└─ 🎯 {{ $t('helper.concepts.subclaim3') }}</div>
                        <div class="ml-18">└─ 📄 {{ $t('helper.concepts.evidence4') }}</div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- Evidence Types -->
                <div class="modern-section">
                  <h2 class="text-3xl font-bold section-title-pink-red mb-6">
                    {{ $t('helper.concepts.evidenceTypesTitle') }}
                  </h2>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="evidence-type-card">
                          <p class="font-bold mb-2 text-gray-900">
                            {{ $t('helper.concepts.evidenceType1') }}
                          </p>
                          <p class="text-sm text-gray-700">
                            {{ $t('helper.concepts.evidenceType1Desc') }}
                          </p>
                        </div>
                        <div class="evidence-type-card">
                          <p class="font-bold mb-2 text-gray-900">
                            {{ $t('helper.concepts.evidenceType2') }}
                          </p>
                          <p class="text-sm text-gray-700">
                            {{ $t('helper.concepts.evidenceType2Desc') }}
                          </p>
                        </div>
                        <div class="evidence-type-card">
                          <p class="font-bold mb-2 text-gray-900">
                            {{ $t('helper.concepts.evidenceType3') }}
                          </p>
                          <p class="text-sm text-gray-700">
                            {{ $t('helper.concepts.evidenceType3Desc') }}
                          </p>
                        </div>
                        <div class="evidence-type-card">
                          <p class="font-bold mb-2 text-gray-900">
                            {{ $t('helper.concepts.evidenceType4') }}
                          </p>
                          <p class="text-sm text-gray-700">
                            {{ $t('helper.concepts.evidenceType4Desc') }}
                          </p>
                        </div>
                        <div class="evidence-type-card">
                          <p class="font-bold mb-2 text-gray-900">
                            {{ $t('helper.concepts.evidenceType5') }}
                          </p>
                          <p class="text-sm text-gray-700">
                            {{ $t('helper.concepts.evidenceType5Desc') }}
                          </p>
                        </div>
                        <div class="evidence-type-card">
                          <p class="font-bold mb-2 text-gray-900">
                            {{ $t('helper.concepts.evidenceType6') }}
                          </p>
                          <p class="text-sm text-gray-700">
                            {{ $t('helper.concepts.evidenceType6Desc') }}
                          </p>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Tab 3: Standards -->
        <v-tab-item>
          <v-card class="mt-6 modern-card" elevation="0">
            <v-card-text>
              <div class="space-y-8">
                <!-- Overview -->
                <div>
                  <h2 class="text-3xl font-bold section-title-cyan-blue mb-4">
                    {{ $t('helper.standards.title') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.standards.overview') }}
                  </p>
                </div>

                <!-- CRA (Cyber Resilience Act) -->
                <div class="modern-section">
                  <h3 class="text-2xl font-bold text-gray-800 mb-3">
                    {{ $t('helper.standards.craTitle') }}
                  </h3>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.standards.craDesc') }}
                  </p>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <p class="font-bold mb-3 text-gray-900">
                        {{ $t('helper.standards.craKeyPoints') }}
                      </p>
                      <ul class="list-disc list-inside space-y-2 text-sm text-gray-700">
                        <li>{{ $t('helper.standards.craPoint1') }}</li>
                        <li>{{ $t('helper.standards.craPoint2') }}</li>
                        <li>{{ $t('helper.standards.craPoint3') }}</li>
                        <li>{{ $t('helper.standards.craPoint4') }}</li>
                      </ul>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- IEC 62443 Standards -->
                <div class="modern-section">
                  <h3 class="text-2xl font-bold text-gray-800 mb-4">
                    {{ $t('helper.standards.iecTitle') }}
                  </h3>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.standards.iecDesc') }}
                  </p>

                  <!-- IEC 62443-4-1 -->
                  <div class="mb-6">
                    <div class="standard-tag">{{ $t('helper.standards.iec41Title') }}</div>
                    <p class="text-sm mb-3 mt-3 text-gray-700">
                      {{ $t('helper.standards.iec41Desc') }}
                    </p>
                    <v-card class="modern-highlight-card">
                      <v-card-text>
                        <ul class="list-disc list-inside space-y-2 text-sm text-gray-700">
                          <li>{{ $t('helper.standards.iec41Point1') }}</li>
                          <li>{{ $t('helper.standards.iec41Point2') }}</li>
                          <li>{{ $t('helper.standards.iec41Point3') }}</li>
                        </ul>
                      </v-card-text>
                    </v-card>
                  </div>

                  <!-- IEC 62443-4-2 -->
                  <div class="mb-6">
                    <div class="standard-tag">{{ $t('helper.standards.iec42Title') }}</div>
                    <p class="text-sm mb-3 mt-3 text-gray-700">
                      {{ $t('helper.standards.iec42Desc') }}
                    </p>
                    <v-card class="modern-highlight-card">
                      <v-card-text>
                        <ul class="list-disc list-inside space-y-2 text-sm text-gray-700">
                          <li>{{ $t('helper.standards.iec42Point1') }}</li>
                          <li>{{ $t('helper.standards.iec42Point2') }}</li>
                          <li>{{ $t('helper.standards.iec42Point3') }}</li>
                        </ul>
                      </v-card-text>
                    </v-card>
                  </div>

                  <!-- IEC 62443-2-4 -->
                  <div>
                    <div class="standard-tag">{{ $t('helper.standards.iec24Title') }}</div>
                    <p class="text-sm mb-3 mt-3 text-gray-700">
                      {{ $t('helper.standards.iec24Desc') }}
                    </p>
                    <v-card class="modern-highlight-card">
                      <v-card-text>
                        <ul class="list-disc list-inside space-y-2 text-sm text-gray-700">
                          <li>{{ $t('helper.standards.iec24Point1') }}</li>
                          <li>{{ $t('helper.standards.iec24Point2') }}</li>
                          <li>{{ $t('helper.standards.iec24Point3') }}</li>
                        </ul>
                      </v-card-text>
                    </v-card>
                  </div>
                </div>

                <!-- Standards Mapping -->
                <div class="modern-section">
                  <h3 class="text-2xl font-bold text-gray-800 mb-3">
                    {{ $t('helper.standards.mapping') }}
                  </h3>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <p class="text-sm mb-4 text-gray-700">
                        {{ $t('helper.standards.mappingDesc') }}
                      </p>
                      <div class="text-sm space-y-3">
                        <p>
                          <span class="font-bold text-primary-600">CRA:</span>
                          <span class="text-gray-700">{{ $t('helper.standards.mappingCra') }}</span>
                        </p>
                        <p>
                          <span class="font-bold text-primary-600">IEC 62443:</span>
                          <span class="text-gray-700">{{ $t('helper.standards.mappingIec') }}</span>
                        </p>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Tab 4: Tools -->
        <v-tab-item>
          <v-card class="mt-6 modern-card" elevation="0">
            <v-card-text>
              <div class="space-y-8">
                <!-- Threat Spider -->
                <div class="modern-section tool-card-section-th">
                  <h2 class="text-3xl font-bold section-title-red-orange mb-4">
                    {{ $t('helper.tools.threatSpiderTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.tools.threatSpiderDesc') }}
                  </p>
                  <v-card class="modern-highlight-card mb-4">
                    <v-card-text>
                      <p class="font-bold mb-3 text-gray-900">
                        {{ $t('helper.tools.threatSpiderUses') }}
                      </p>
                      <ul class="list-disc list-inside space-y-2 text-sm text-gray-700">
                        <li>{{ $t('helper.tools.threatSpiderUse1') }}</li>
                        <li>{{ $t('helper.tools.threatSpiderUse2') }}</li>
                        <li>{{ $t('helper.tools.threatSpiderUse3') }}</li>
                        <li>{{ $t('helper.tools.threatSpiderUse4') }}</li>
                      </ul>
                    </v-card-text>
                  </v-card>
                  <v-card class="modern-highlight-card mb-4">
                    <v-card-text>
                      <p class="font-bold mb-3 text-gray-900">
                        {{ $t('helper.tools.riskAssessmentSteps') }}
                      </p>
                      <ol class="list-decimal list-inside space-y-2 text-sm text-gray-700">
                        <li>{{ $t('helper.tools.riskStep1') }}</li>
                        <li>{{ $t('helper.tools.riskStep2') }}</li>
                        <li>{{ $t('helper.tools.riskStep3') }}</li>
                        <li>{{ $t('helper.tools.riskStep4') }}</li>
                      </ol>
                    </v-card-text>
                  </v-card>
                  <v-alert type="info" outlined class="modern-alert">
                    {{ $t('helper.tools.threatSpiderTip') }}
                  </v-alert>
                </div>

                <!-- SAC Composer Chat -->
                <div class="modern-section tool-card-section-sc">
                  <h2 class="text-3xl font-bold section-title-green-emerald mb-4">
                    {{ $t('helper.tools.sacComposerTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.tools.sacComposerDesc') }}
                  </p>
                  <v-alert type="success" outlined class="modern-alert">
                    {{ $t('helper.tools.sacComposerTip') }}
                  </v-alert>
                </div>

                <!-- Exclaim Scorer tool -->
                <div class="modern-section tool-card-section-es">
                  <h2 class="text-3xl font-bold section-title-yellow-amber mb-4">
                    {{ $t('helper.tools.exclaimScorerTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.tools.exclaimScorerDescription') }}
                  </p>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <p class="text-sm text-gray-700">
                        {{ $t('helper.tools.exclaimScorerContent') }}
                      </p>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- SBOM (Software Bill of Materials) -->
                <div class="modern-section tool-card-section-vim">
                  <h2 class="text-3xl font-bold section-title-violet-purple mb-4">
                    {{ $t('helper.tools.sbomTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.tools.sbomDesc') }}
                  </p>
                  <v-card class="modern-highlight-card mb-4">
                    <v-card-text>
                      <p class="font-bold mb-3 text-gray-900">
                        {{ $t('helper.tools.sbomIncludes') }}
                      </p>
                      <ul class="list-disc list-inside space-y-2 text-sm text-gray-700">
                        <li>{{ $t('helper.tools.sbomItem1') }}</li>
                        <li>{{ $t('helper.tools.sbomItem2') }}</li>
                        <li>{{ $t('helper.tools.sbomItem3') }}</li>
                        <li>{{ $t('helper.tools.sbomItem4') }}</li>
                      </ul>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- SBOM Visualizer -->
                <div class="modern-section tool-card-section-sv">
                  <h2 class="text-3xl font-bold section-title-teal-cyan mb-4">
                    {{ $t('helper.tools.sbomVisualizerTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.tools.sbomVisualizerDesc') }}
                  </p>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <p class="text-sm text-gray-700">
                        {{ $t('helper.tools.sbomVisualizerContent') }}
                      </p>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- Dependency Track -->
                <div class="modern-section tool-card-section-dp">
                  <h2 class="text-3xl font-bold section-title-pink-rose mb-4">
                    {{ $t('helper.tools.dependencyTrackTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.tools.dependencyTrackDescription') }}
                  </p>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <p class="text-sm text-gray-700">
                        {{ $t('helper.tools.dependencyTrackContent') }}
                      </p>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- PlantUML -->
                <div class="modern-section tool-card-section-pu">
                  <h2 class="text-3xl font-bold section-title-indigo-purple mb-4">
                    {{ $t('helper.tools.plantumlTitle') }}
                  </h2>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.tools.plantumlDesc') }}
                  </p>
                  <v-alert type="info" outlined class="modern-alert">
                    {{ $t('helper.tools.plantumlTip') }}
                  </v-alert>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Tab 5: Workflow -->
        <v-tab-item>
          <v-card class="mt-6 modern-card" elevation="0">
            <v-card-text>
              <div class="space-y-8">
                <!-- Getting Started -->
                <div>
                  <h2 class="text-3xl font-bold section-title-blue mb-4">
                    {{ $t('helper.workflow.gettingStarted') }}
                  </h2>
                  <p class="text-base leading-relaxed text-gray-700">
                    {{ $t('helper.workflow.gettingStartedDesc') }}
                  </p>
                </div>

                <!-- Step-by-Step Workflow -->
                <div>
                  <h3 class="text-2xl font-bold text-gray-800 mb-6">
                    {{ $t('helper.workflow.stepByStep') }}
                  </h3>

                  <v-timeline class="workflow-timeline">
                    <!-- Step 1 -->
                    <v-timeline-item fill-dot color="primary-600" class="workflow-item">
                      <template v-slot:icon>
                        <div class="workflow-step-number step-1">1</div>
                      </template>
                      <div class="workflow-content">
                        <span class="font-bold text-lg text-gray-900">
                          {{ $t('helper.workflow.step1Title') }}
                        </span>
                        <p class="text-sm mt-2 text-gray-700">
                          {{ $t('helper.workflow.step1Desc') }}
                        </p>
                      </div>
                    </v-timeline-item>

                    <!-- Step 2 -->
                    <v-timeline-item fill-dot color="blue-600" class="workflow-item">
                      <template v-slot:icon>
                        <div class="workflow-step-number step-2">2</div>
                      </template>
                      <div class="workflow-content">
                        <span class="font-bold text-lg text-gray-900">
                          {{ $t('helper.workflow.step2Title') }}
                        </span>
                        <p class="text-sm mt-2 text-gray-700">
                          {{ $t('helper.workflow.step2Desc') }}
                        </p>
                      </div>
                    </v-timeline-item>

                    <!-- Step 3 -->
                    <v-timeline-item fill-dot color="purple-600" class="workflow-item">
                      <template v-slot:icon>
                        <div class="workflow-step-number step-3">3</div>
                      </template>
                      <div class="workflow-content">
                        <span class="font-bold text-lg text-gray-900">
                          {{ $t('helper.workflow.step3Title') }}
                        </span>
                        <p class="text-sm mt-2 text-gray-700">
                          {{ $t('helper.workflow.step3Desc') }}
                        </p>
                      </div>
                    </v-timeline-item>

                    <!-- Step 4 -->
                    <v-timeline-item fill-dot color="green-600" class="workflow-item">
                      <template v-slot:icon>
                        <div class="workflow-step-number step-4">4</div>
                      </template>
                      <div class="workflow-content">
                        <span class="font-bold text-lg text-gray-900">
                          {{ $t('helper.workflow.step4Title') }}
                        </span>
                        <p class="text-sm mt-2 text-gray-700">
                          {{ $t('helper.workflow.step4Desc') }}
                        </p>
                      </div>
                    </v-timeline-item>

                    <!-- Step 5 -->
                    <v-timeline-item fill-dot color="orange-600" class="workflow-item">
                      <template v-slot:icon>
                        <div class="workflow-step-number step-5">5</div>
                      </template>
                      <div class="workflow-content">
                        <span class="font-bold text-lg text-gray-900">
                          {{ $t('helper.workflow.step5Title') }}
                        </span>
                        <p class="text-sm mt-2 text-gray-700">
                          {{ $t('helper.workflow.step5Desc') }}
                        </p>
                      </div>
                    </v-timeline-item>

                    <!-- Step 6 -->
                    <v-timeline-item fill-dot color="red-600" class="workflow-item">
                      <template v-slot:icon>
                        <div class="workflow-step-number step-6">6</div>
                      </template>
                      <div class="workflow-content">
                        <span class="font-bold text-lg text-gray-900">
                          {{ $t('helper.workflow.step6Title') }}
                        </span>
                        <p class="text-sm mt-2 text-gray-700">
                          {{ $t('helper.workflow.step6Desc') }}
                        </p>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                </div>

                <!-- Certification Process -->
                <div class="modern-section">
                  <h3 class="text-2xl font-bold text-gray-800 mb-4">
                    {{ $t('helper.workflow.certificationProcess') }}
                  </h3>
                  <p class="text-base leading-relaxed mb-4 text-gray-700">
                    {{ $t('helper.workflow.certificationProcessDesc') }}
                  </p>
                  <v-card class="modern-highlight-card mb-4">
                    <v-card-text>
                      <p class="font-bold mb-4 text-gray-900">
                        {{ $t('helper.workflow.certificationSteps') }}
                      </p>
                      <ol class="list-decimal list-inside space-y-3 text-sm">
                        <li class="text-gray-700">
                          <span class="font-bold text-primary-600"
                            >{{ $t('helper.workflow.certStep1Title') }}:</span
                          >
                          {{ $t('helper.workflow.certStep1Desc') }}
                        </li>
                        <li class="text-gray-700">
                          <span class="font-bold text-primary-600"
                            >{{ $t('helper.workflow.certStep2Title') }}:</span
                          >
                          {{ $t('helper.workflow.certStep2Desc') }}
                        </li>
                        <li class="text-gray-700">
                          <span class="font-bold text-primary-600"
                            >{{ $t('helper.workflow.certStep3Title') }}:</span
                          >
                          {{ $t('helper.workflow.certStep3Desc') }}
                        </li>
                        <li class="text-gray-700">
                          <span class="font-bold text-primary-600"
                            >{{ $t('helper.workflow.certStep4Title') }}:</span
                          >
                          {{ $t('helper.workflow.certStep4Desc') }}
                        </li>
                      </ol>
                    </v-card-text>
                  </v-card>
                </div>

                <!-- Tips and Best Practices -->
                <div class="modern-section">
                  <h3 class="text-2xl font-bold text-gray-800 mb-4">
                    {{ $t('helper.workflow.bestPractices') }}
                  </h3>
                  <v-card class="modern-highlight-card">
                    <v-card-text>
                      <ul class="list-disc list-inside space-y-3 text-sm text-gray-700">
                        <li>{{ $t('helper.workflow.bestPractice1') }}</li>
                        <li>{{ $t('helper.workflow.bestPractice2') }}</li>
                        <li>{{ $t('helper.workflow.bestPractice3') }}</li>
                        <li>{{ $t('helper.workflow.bestPractice4') }}</li>
                        <li>{{ $t('helper.workflow.bestPractice5') }}</li>
                      </ul>
                    </v-card-text>
                  </v-card>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-container>
  </v-main>
</template>

<script>
import { defineComponent } from '@vue/composition-api';
import util from '@/mixins/util.js';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default defineComponent({
  name: 'Helper',
  components: {
    Breadcrumbs,
  },
  mixins: [util],
  data() {
    return {
      activeTab: 0,
      featuresList: [
        {
          icon: 'mdi-shield-check',
          title: 'Security Assurance',
          description:
            'Build comprehensive security assurance cases with claims, sub-claims, and evidence',
        },
        {
          icon: 'mdi-file-document-multiple',
          title: 'Evidence Management',
          description: 'Organize and manage all your security evidence in one centralized location',
        },
        {
          icon: 'mdi-spider',
          title: 'Threat Modeling',
          description: 'Identify and analyze potential threats to your system using Threat Spider',
        },
        {
          icon: 'mdi-package-variant',
          title: 'SBOM Tracking',
          description: 'Maintain visibility of all software components and dependencies',
        },
        {
          icon: 'mdi-target',
          title: 'Risk Assessment',
          description: 'Evaluate and mitigate security risks in your systems using Threat Spider',
        },
        {
          icon: 'mdi-certificate',
          title: 'Compliance Ready',
          description: 'Ensure your systems meet CRA, IEC 62443, and other regulatory standards',
        },
      ],
    };
  },
  created() {
    // Check if there's a tab query parameter
    if (this.$route.query.tab === 'workflow') {
      this.activeTab = 4; // Workflow tab is at index 4
    }
  },
});
</script>

<style scoped>
/* Modern Helper Styling */

.modern-helper-bg {
  min-height: 100vh;
}

/* Gradient Title Styles */
.gradient-title {
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #1e40af 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-blue {
  background: linear-gradient(135deg, #1e40af, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-blue-purple {
  background: linear-gradient(135deg, #2563eb, #9333ea);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-purple-pink {
  background: linear-gradient(135deg, #9333ea, #db2777);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-indigo-blue {
  background: linear-gradient(135deg, #4f46e5, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-pink-red {
  background: linear-gradient(135deg, #ec4899, #dc2626);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-cyan-blue {
  background: linear-gradient(135deg, #0891b2, #2563eb);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-red-orange {
  background: linear-gradient(135deg, #dc2626, #ea580c);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-green-emerald {
  background: linear-gradient(135deg, #16a34a, #059669);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-yellow-amber {
  background: linear-gradient(135deg, #ca8a04, #b45309);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-violet-purple {
  background: linear-gradient(135deg, #7c3aed, #9333ea);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-teal-cyan {
  background: linear-gradient(135deg, #0d9488, #0891b2);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-pink-rose {
  background: linear-gradient(135deg, #ec4899, #e11d48);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.section-title-indigo-purple {
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

/* Header Gradient Section */
.header-gradient-section {
  padding: 2rem 0;
  animation: fadeInDown 0.6s ease-out;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fade-in {
  animation: fadeIn 0.8s ease-out;
}

/* Modern Tabs */
.modern-tabs {
  border-bottom: 2px solid #e5e7eb;
  background: transparent !important;
}

.modern-tabs ::v-deep .v-tabs-bar {
  background: transparent;
}

.modern-tabs ::v-deep .v-tab {
  font-weight: 600;
  font-size: 1.05rem;
  color: #6b7280;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-tabs ::v-deep .v-tab:hover {
  color: #3b82f6;
  transform: translateY(-2px);
}

.modern-tabs ::v-deep .v-tab--active {
  color: #2563eb;
  font-weight: 700;
}

.modern-tabs ::v-deep .v-tabs-slider {
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  height: 4px;
}

/* Tab Content */
.modern-tab-content {
  margin-top: 1rem;
}

/* Modern Card */
.modern-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid #0354f733;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.modern-card:hover {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

/* Modern Highlight Card */
.modern-highlight-card {
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-highlight-card:hover {
  background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
  border-color: #bfdbfe;
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.1);
}

/* Modern Section */
.modern-section {
  padding: 0;
  transition: all 0.3s ease;
}

/* Modern List Item */
.modern-list-item {
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-list-item:hover {
  background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
  border-color: #bfdbfe;
  transform: translateX(8px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.12);
}

/* Feature Icon Wrapper */
.feature-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-list-item:hover .feature-icon-wrapper {
  background: linear-gradient(135deg, #1d4ed8, #1e40af);
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
}

/* Modern Alert */
.modern-alert {
  border-radius: 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-color: #7dd3fc !important;
  transition: all 0.3s ease;
}

.modern-alert:hover {
  box-shadow: 0 8px 25px rgba(6, 182, 212, 0.15);
}

/* Hierarchy Card */
.hierarchy-card {
  background: linear-gradient(135deg, #fafaf9 0%, #f5f5f4 100%);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace;
}

/* Evidence Type Card */
.evidence-type-card {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.evidence-type-card:hover {
  background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
  border-color: #bfdbfe;
  transform: translateY(-6px);
  box-shadow: 0 12px 35px rgba(59, 130, 246, 0.15);
}

/* Standard Tag */
.standard-tag {
  display: inline-block;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.standard-tag:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

/* Tool Card Section */
.tool-card-section-th {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border-left: 4px solid #ff3c00;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card-section-th:hover {
  border-left-color: #ff1e00;
  box-shadow: 0 10px 35px rgba(59, 130, 246, 0.1);
  transform: translateX(4px);
}
.tool-card-section-sc {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border-left: 4px solid #02b310;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card-section-sc:hover {
  border-left-color: #188302;
  box-shadow: 0 10px 35px rgba(59, 130, 246, 0.1);
  transform: translateX(4px);
}
.tool-card-section-es {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border-left: 4px solid #cc9d02;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card-section-es:hover {
  border-left-color: #b68c02;
  box-shadow: 0 10px 35px rgba(59, 130, 246, 0.1);
  transform: translateX(4px);
}
.tool-card-section-vim {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border-left: 4px solid #5502f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card-section-vim:hover {
  border-left-color: #4904c9;
  box-shadow: 0 10px 35px rgba(59, 130, 246, 0.1);
  transform: translateX(4px);
}
.tool-card-section-sv {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border-left: 4px solid #06ac7a;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card-section-sv:hover {
  border-left-color: #088862;
  box-shadow: 0 10px 35px rgba(59, 130, 246, 0.1);
  transform: translateX(4px);
}
.tool-card-section-dp {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border-left: 4px solid #d606cc;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card-section-dp:hover {
  border-left-color: #b805af;
  box-shadow: 0 10px 35px rgba(59, 130, 246, 0.1);
  transform: translateX(4px);
}
.tool-card-section-pu {
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border-left: 4px solid #3b82f6;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tool-card-section-pu:hover {
  border-left-color: #2563eb;
  box-shadow: 0 10px 35px rgba(59, 130, 246, 0.1);
  transform: translateX(4px);
}

/* Workflow Timeline */
.workflow-timeline {
  position: relative;
}

.workflow-item {
  transition: all 0.3s ease;
}

.workflow-item:hover .workflow-content {
  transform: translateX(8px);
}

.workflow-content {
  transition: all 0.3s ease;
}

/* Workflow Step Numbers */
.workflow-step-number {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.5rem;
  color: white;
  position: relative;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.workflow-step-number::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: inherit;
  opacity: 0.2;
  animation: pulse-ring 2s infinite;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.4;
  }
  50% {
    opacity: 0.2;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

.workflow-item:hover .workflow-step-number {
  transform: scale(1.15);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25);
}

/* Color variants for each step */
.step-1 {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
}

.step-1:hover {
  background: linear-gradient(135deg, #1e40af, #2563eb);
}

.step-2 {
  background: linear-gradient(135deg, #5900ff, #5c2fda);
}

.step-2:hover {
  background: linear-gradient(135deg, #4701c7, #4b26b1);
}

.step-3 {
  background: linear-gradient(135deg, #9333ea, #a855f7);
}

.step-3:hover {
  background: linear-gradient(135deg, #7c3aed, #9333ea);
}

.step-4 {
  background: linear-gradient(135deg, #16a34a, #22c55e);
}

.step-4:hover {
  background: linear-gradient(135deg, #15803d, #16a34a);
}

.step-5 {
  background: linear-gradient(135deg, #ea580c, #f97316);
}

.step-5:hover {
  background: linear-gradient(135deg, #c2410c, #ea580c);
}

.step-6 {
  background: linear-gradient(135deg, #dc2626, #ef4444);
}

.step-6:hover {
  background: linear-gradient(135deg, #b91c1c, #dc2626);
}

/* Utility */
.ml-18 {
  margin-left: 4.5rem;
}

/* Smooth transitions for all Vuetify components */
::v-deep .v-card,
::v-deep .v-alert {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #3b82f6, #2563eb);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #2563eb, #1e40af);
}
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}

.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}
</style>
